import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractComponent, CompanyCrmLists, CompanyService, LeadQualityConfig, LeadQualityCriterion } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

type IcpList = 'segments' | 'employeeBands';

/**
 * Lead-quality criteria, thresholds and health bands
 * (SPEC-2026-08-crm-pipeline-deals-radar — RF-34, AC-18), plus the E3 blocks: score colour
 * bands, connection cadence and the ideal customer profile
 * (SPEC-2026-09-crm-qualification-prioritisation — RF-13, RF-17, RF-28, RF-38).
 *
 * Routed page of the CRM group: `/kanban/lead-quality`, guarded by AuthGuardAdmin.
 * It used to be a card embedded in /config, which reads as attendance/company
 * settings and buried a CRM screen inside the wrong module.
 *
 * A company that never configured its own follows the platform default, and the screen
 * says so out loud — silently showing editable defaults would make an administrator
 * think the company had already been configured. The API merges the platform defaults
 * under a stored E1 configuration, so the E3 blocks always arrive filled.
 */
@Component({
  selector: 'lead-quality-config-component',
  templateUrl: './lead-quality-config.component.html',
  styleUrls: ['./lead-quality-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LeadQualityConfigComponent extends AbstractComponent implements OnInit {

  config?: LeadQualityConfig;
  /** The company's segment and size lists — what the ideal-profile rule is stated over (E3 RF-28). */
  crmLists?: CompanyCrmLists;
  isDefault = true;
  override loading = true;
  saving = false;

  constructor(injector: Injector, private readonly companyService: CompanyService) {
    super(injector);
  }

  ngOnInit(): void {
    this.load();
    this.companyService
      .getCrmLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (lists) => (this.crmLists = lists),
        error: () => (this.crmLists = { segments: [], employeeBands: [] }),
      });
  }

  private load(): void {
    this.companyService
      .getLeadQualityConfig()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Deep clone: editing the response must not mutate what the screen compares
          // against when the administrator discards changes.
          this.config = JSON.parse(JSON.stringify(result.config));
          this.config.icp ??= { segments: [], employeeBands: [] };
          this.isDefault = result.isDefault;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.alertService.error(this.t('leadQuality.config.loadError'));
        },
      });
  }

  /** Total weight of the score criteria — the real denominator (RF-41). */
  get totalWeight(): number {
    return (this.config?.scoreCriteria ?? []).reduce((total, criterion) => total + (criterion.weight ?? 0), 0);
  }

  /**
   * The default sums to 100, which makes the score read as a percentage. A company is
   * free to diverge — the engine normalises over the applicable weight either way — so
   * this is a hint, not a validation error.
   */
  get weightHint(): string {
    return this.totalWeight === 100 ? 'leadQuality.config.weightExact' : 'leadQuality.config.weightDiverges';
  }

  criterionLabel(criterion: LeadQualityCriterion): string {
    return `kanban.scoreCriterion.${criterion.key}`;
  }

  blockLabel(block?: string): string {
    return block ? `kanban.scoreBlock.${block}` : 'kanban.scoreBlock.OTHER';
  }

  bandLabel(band: string): string {
    return `kanban.healthBand.${band.toLowerCase()}`;
  }

  /** A criterion sourced from the assistance layer is reserved and inert at weight 0. */
  isCopilotSourced(criterion: LeadQualityCriterion): boolean {
    return criterion?.source === 'COPILOT';
  }

  /**
   * What the profile can be stated over: the company's list, plus any value the profile still holds
   * after leaving it (removed or renamed). The rule keeps applying such a value, so it is shown —
   * ticked and flagged — to be unticked; hidden, the screen would read "nothing ticked" meanwhile.
   */
  icpOptions(list: IcpList): Array<string> {
    if (!this.crmLists) return [];
    const offered = this.crmLists[list] ?? [];
    return [...offered, ...(this.config?.icp?.[list] ?? []).filter((value) => !offered.includes(value))];
  }

  /** A value the profile holds that the company's list no longer offers. */
  isOrphanIcp(list: IcpList, value: string): boolean {
    return !(this.crmLists?.[list] ?? []).includes(value);
  }

  /** E3 RF-28: a value is inside the profile when it is ticked; an empty list constrains nothing (RF-31). */
  isInIcp(list: IcpList, value: string): boolean {
    return (this.config?.icp?.[list] ?? []).includes(value);
  }

  toggleIcp(list: IcpList, value: string): void {
    if (!this.config) return;

    const icp = this.config.icp ?? { segments: [], employeeBands: [] };
    const current = icp[list] ?? [];
    this.config.icp = {
      ...icp,
      [list]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    };
  }

  save(): void {
    if (!this.config || this.saving) return;

    this.saving = true;

    this.companyService
      .updateLeadQualityConfig(this.config)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving = false;
          this.isDefault = false;
          // Saving enqueues a recompute of the company's open deals, so the board will
          // not reflect the new criteria instantly — say so instead of implying it did.
          this.alertService.success(this.t('leadQuality.config.saved'));
        },
        error: (error) => {
          this.saving = false;
          this.alertService.error(error?.error?.message ?? this.t('leadQuality.config.saveError'));
        },
      });
  }

  restoreDefault(): void {
    // Reloading with the company's own config removed is a backend concern; here the
    // administrator simply reloads what is in force and starts over.
    this.loading = true;
    this.load();
  }
}
